import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { ShieldCheck, AlertTriangle, Clock } from "lucide-react";

const KIND_LABELS = {
  gas_safety: "Gas Safety",
  eicr: "EICR",
  epc: "EPC",
  pat_test: "PAT Test",
  legionella: "Legionella Risk",
  insurance: "Insurance",
  landlord_licence: "Landlord Licence",
  fire_alarm: "Fire Alarm",
  carbon_monoxide: "CO Alarm",
  fire_risk: "Fire Risk",
};

export default function CompliancePage() {
  const [data, setData] = useState({ kinds: [], at_risk: [], window_days: 60 });
  useEffect(() => { api.get("/compliance/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const total = data.kinds.reduce((n, k) => n + k.total, 0);
  const expired = data.kinds.reduce((n, k) => n + k.expired, 0);
  const expiring = data.kinds.reduce((n, k) => n + k.expiring_soon, 0);

  return (
    <Layout title="Compliance" subtitle="Gas safety, EICR, EPC and other statutory certificates">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="compliance-top-stats">
        <StatCard label="Tracked certificates" value={total} tone="slate" />
        <StatCard label="Expired" value={expired} tone="rose" testId="compliance-stat-expired" />
        <StatCard label={`Expiring in ${data.window_days}d`} value={expiring} tone="amber" testId="compliance-stat-expiring" />
        <StatCard label="On track" value={Math.max(0, total - expired - expiring)} tone="emerald" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {data.kinds.map((k) => (
          <div key={k.kind} className="ns-card p-4" data-testid={`compliance-kind-${k.kind}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-serif text-base">{KIND_LABELS[k.kind] || k.kind}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{k.total} tracked · {k.not_tracked || 0} untracked properties</div>
              </div>
              {k.expired > 0 ? (
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              ) : k.expiring_soon > 0 ? (
                <Clock className="w-5 h-5 text-amber-600" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {k.expired > 0 && <Chip tone="rose">{k.expired} expired</Chip>}
              {k.expiring_soon > 0 && <Chip tone="amber">{k.expiring_soon} soon</Chip>}
              {k.ok > 0 && <Chip tone="emerald">{k.ok} ok</Chip>}
              {k.missing_date > 0 && <Chip tone="slate">{k.missing_date} no date</Chip>}
            </div>
          </div>
        ))}
      </div>

      <div className="ns-card p-4">
        <div className="ns-label mb-3">Expiring soon / expired · next 60 days</div>
        {data.at_risk.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Nothing on fire 🧯</div>
        ) : (
          <table className="w-full text-sm" data-testid="compliance-at-risk-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2">Property</th><th className="py-2">Certificate</th><th className="py-2">Expires</th><th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.at_risk.map((r, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2 text-xs">{r.property_name}</td>
                  <td className="py-2 text-xs">{KIND_LABELS[r.kind] || r.kind}</td>
                  <td className="py-2 text-xs text-slate-500">{r.expires_at}</td>
                  <td className="py-2">
                    <Chip tone={r.status === "expired" ? "rose" : "amber"}>
                      {r.status === "expired" ? "Expired" : "Soon"}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, tone, testId }) {
  const bg = tone === "rose" ? "bg-rose-50 text-rose-800" : tone === "amber" ? "bg-amber-50 text-amber-800" : tone === "emerald" ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-800";
  return (
    <div className={`${bg} p-3 rounded-xl border border-slate-100`} data-testid={testId}>
      <div className="ns-label">{label}</div>
      <div className="font-serif text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function Chip({ tone, children }) {
  const bg = tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-100" : tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-100" : tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-700 border-slate-100";
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${bg}`}>{children}</span>;
}
